import { TestId } from '@components/test-ids';
import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import React, { CSSProperties, useState } from 'react';
import ReactMapboxGl, { Marker, ZoomControl } from 'react-mapbox-gl';
import { Theme, ThemeProps } from '@theme/base';
import { textShadowMixins } from '@styles/mixins';
import { Icon } from '../generic-icon/Icon';
import { useI18NextContext } from '@components/hooks';
import { Icons } from '@model/common/icons';
import { LazyLoadBackgroundImage } from '../lazy-load';
import { EnvConfig } from '@model/config/env-config';
import 'mapbox-gl/dist/mapbox-gl.css';

/* ***************** *
 *       Types       *
 * ***************** */
export interface MapProps {
  theme?: Theme;
  testId?: string;
  mapMarkerLabel: string;
  latitude: number;
  longitude: number;
  containerStyle?: CSSProperties;
  initialZoom?: number;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.map(({ theme }: ThemeProps) => ({ marginTop: theme.custom.spacing.xLarge }));

const MarkerLabel = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  color: theme.custom.colors.group11.base,
  alignItems: 'center',
  maxWidth: 200,
  marginTop: -30,
  marginLeft: -15,
  textShadow: textShadowMixins(theme.custom.colors.white).stroke,

  ['span']: {
    paddingLeft: theme.custom.spacing.xSmall
  }
}));

const markerIconStyles = (theme: Theme) => ({
  fontSize: '3rem',
  color: theme.custom.colors.group11.base
});

const defaultContainerStyles = {
  width: '100%',
  height: 370,
  maxHeight: '50vh'
};

/* ****************** *
 *    MapComponent    *
 * ****************** */

const MapBox = ReactMapboxGl({
  accessToken: EnvConfig.get().MAP_BOX_API_KEY,
  scrollZoom: false,
  doubleClickZoom: false
});

const ClickToViewContainer: any = styled.div(({ theme }: ThemeProps) => ({
  borderRadius: theme.custom.spacing.small,
  overflow: 'hidden',
  position: 'relative',
  maxWidth: '100%',
  height: 370,
  maxHeight: '50vh',
  textAlign: 'center',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer'
}));

const ClickToViewText = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  alignItems: 'center',
  textShadow: textShadowMixins(theme.custom.colors.black).heavy,
  p: {
    display: 'inline',
    ...(theme.custom.typography.paragraph as any),
    fontWeight: '500',
    color: theme.custom.colors.white
  },
  zIndex: 1
}));

const MapIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  fontSize: '2rem',
  marginRight: 10,
  color: theme.custom.colors.white
}));

export const MapComponent = (props: MapProps) => {
  const { theme, mapMarkerLabel, latitude, longitude, containerStyle, initialZoom = 15 } = props;
  const t: any = useI18NextContext();
  const [visible, setVisible] = useState(false);

  /* *** RENDERERS *** */
  if (!visible) {
    return (
      <ClickToViewContainer onClick={() => setVisible(true)}>
        <LazyLoadBackgroundImage src={'/assets/images/hotel-map.jpg'} quality={80} />
        <ClickToViewText>
          <MapIcon name={Icons.MAP} />
          <p>{t('map__click-to-view--title')}</p>
        </ClickToViewText>
      </ClickToViewContainer>
    );
  }
  return (
    <Container data-testid={props.testId || TestId.map.main}>
      <MapBox
        data-testid={TestId.map.mapBox}
        style="mapbox://styles/mapbox/streets-v9?optimize=true"
        containerStyle={containerStyle || defaultContainerStyles}
        center={[longitude, latitude]}
        zoom={[initialZoom]}
      >
        <ZoomControl />
        <Marker coordinates={[longitude, latitude]} anchor="left">
          <MarkerLabel>
            <Icon name={'mhi-map-pin'} css={markerIconStyles(theme!)} />
            <span>{mapMarkerLabel}</span>
          </MarkerLabel>
        </Marker>
      </MapBox>
    </Container>
  );
};

export const Map = withTheme(MapComponent);
export default withTheme(MapComponent);
